import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import CourseTable from "../../components/Tables/CourseTable";
import { API } from "../../shared/api/Api";
import { useSelector } from "react-redux"; // useDispatch removed
import { selectUserInformations } from "../../features/authSlice";
import { showToast } from "../../utils/utils";

const DEBOUNCE_DELAY = 700;

const CoursePage = () => {
  const [activeTab, setActiveTab] = useState("All Courses");
  const [reFetchData, setReFetchData] = useState(false);
  const [courses, setCourses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedItem, setSelectedItem] = useState(0);
  const [loader, setLoader] = useState(true);
  const [orderBy, setOrderBy] = useState("created_at"); // Default sorting column
  const [orderDir, setOrderDir] = useState("DESC"); // Default sorting direction

  const user = useSelector(selectUserInformations);
  const isInstructor = user.instructor ?? false;
  const isStudent = user.student ?? false;

  const fetchPersonCourses = useCallback(async () => {
    try {
      const endpoint = isInstructor
        ? `${API.Instructors.app_instructors_allCoursesAssignedToInstructor}/${isInstructor.id}`
        : `${API.Students.app_students_allCoursesForStudent}/${isStudent.id}`;

      const { data } = await axios.get(endpoint, {
        params: {
          page: currentPage,
          search: debouncedSearch,
          orderBy: orderBy,
          orderDir: orderDir,
        },
        headers: { Authorization: `Bearer ${user.token}` },
      });

      if (data.status === 200 && data.errorCode === 0) {
        setCourses(data.data.items);
        setCurrentPage(data.data.current_page);
        setTotalPages(data.data.last_page);
      } else {
        showToast("Error", "Failed to fetch courses", "error");
      }
    } catch (error) {
      showToast(
        "Error",
        error.response?.data?.message || "An error occurred",
        "error"
      );
    }
  }, [
    currentPage,
    debouncedSearch,
    orderBy,
    orderDir,
    user.token,
    isInstructor,
    isStudent,
  ]);

  const fetchCourses = useCallback(async () => {
    try {
      const { data } = await axios.get(API.Courses.app_courses_all, {
        params: {
          page: currentPage,
          search: debouncedSearch,
          orderBy: orderBy,
          orderDir: orderDir,
          // Only include category filter if a specific category is selected (not 0 = "All")
          ...(selectedItem && selectedItem !== 0 ? { category: selectedItem } : {}),
        },
        headers: { Authorization: `Bearer ${user.token}` },
      });

      if (data.status === 200 && data.errorCode === 0) {
        setCourses(data.data.items);
        setCurrentPage(data.data.current_page);
        setTotalPages(data.data.last_page);
      } else {
        showToast("Error", "Failed to fetch courses", "error");
      }
    } catch (error) {
      showToast(
        "Error",
        error.response?.data?.message || "An error occurred",
        "error"
      );
    }
  }, [currentPage, debouncedSearch, orderBy, orderDir, selectedItem, user.token]);

  const fetchCategories = useCallback(async () => {
    try {
      const { data } = await axios.get(API.Exposed.allCategories, {
        params: { 
          limit: 100, // Get more categories
        },
        headers: { Authorization: `Bearer ${user.token}` },
      });
      if (data.status === 200 && data.errorCode === 0) {
        // Add "All" option at the beginning
        const allOption = { id: 0, title: "All" };
        setCategories([allOption, ...(data.data.items || [])]);
      }
    } catch (error) {
      console.error("Failed to fetch categories:", error);
      showToast(
        "Error",
        error.response?.data?.message ||
          "An error occurred while fetching categories.",
        "error"
      );
    }
  }, [user.token]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
      setCurrentPage(1); // Reset to the first page on search change
    }, DEBOUNCE_DELAY);

    return () => clearTimeout(handler);
  }, [search]);

  // Reset to first page when category filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedItem]);

  // Fetch categories only once on mount
  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  // Fetch courses when filters change
  useEffect(() => {
    const fetchData = async () => {
      setLoader(true);
      try {
        setCourses([]);
        if (activeTab === "All Courses") {
          await fetchCourses();
        } else {
          await fetchPersonCourses();
        }
      } catch (error) {
        // Log the detailed error for debugging
        console.error("Failed to fetch course data:", error);
        // Show a user-friendly toast
        showToast(
          "Error",
          error.message ||
            "An error occurred while fetching data. Please try again.",
          "error"
        );
      } finally {
        setLoader(false); // Hide loader after fetching is complete
        setReFetchData(false);
      }
    };
    fetchData();
  }, [
    activeTab,
    reFetchData,
    fetchCourses,
    fetchPersonCourses,
    selectedItem,
  ]);

  const handleSort = (column, direction) => {
    setOrderBy(column);
    setOrderDir(direction);
  };


  return (
    <div className="min-h-screen p-2 sl:pt-0 sm:p-2 md:p-4 sl:p-6">
      <CourseTable
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        courses={courses}
        categories={categories}
        currentPage={currentPage}
        totalPages={totalPages}
        onSearchChange={setSearch}
        onPageChange={setCurrentPage}
        isStudent={isStudent}
        setSelectedItem={setSelectedItem}
        loader={loader}
        setReFetchData={setReFetchData}
        onSort={handleSort}
        currentOrderBy={orderBy}
        currentOrderDir={orderDir}
      />
    </div>
  );
};

export default CoursePage;
